/**
 * useAgentChat Hook
 *
 * Хук для управления чатом с AI агентом.
 * Поддерживает треды, streaming сообщений и structured outputs.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  agentService,
  isAgentStreamEventsEnabled,
  type AgentThread,
  type AgentMessage as AgentMessageDTO,
  type AgentStreamEvent,
  type AgentChatInteraction,
  type MessagePart,
} from "@/services/agent.service";
import { ANALYTICS_EVENTS } from "@/lib/analytics/catalog";
import { trackResult, trackUi } from "@/lib/analytics/amplitude";
import type {
  AgentMessage,
  AgentMessageActivityEvent,
  AgentMessageActivityState,
  AgentSuggestion,
  AgentMode,
} from "../types/agent.types";
import type { DocumentScopePayload } from "@/components/features/agent/components/agent-document-scope-state";
import { useBalance } from "@/contexts/BalanceContext";
import { useProjectsRootStore } from "@/stores/projects/createProjectsRootStore";
import { useRecordsRootStore } from "@/stores/records/createRecordsRootStore";
import { useAgentChatCancellationContext } from "../context/AgentChatCancellationContext";
import { refreshStoresForWorkspaceActions } from "./workspace-action-refresh";

interface UseAgentChatOptions {
  /** Callback при изменении треда */
  onThreadChange?: (threadId: string) => void;
  /** Callback при обновлении списка тредов */
  onThreadsUpdate?: (threads: AgentThread[]) => void;
}

interface AgentEditSendOptions {
  editOfMessageId: string;
  confirmWorkspaceActions?: boolean;
}

interface UseAgentChatReturn {
  isThreadsLoaded: boolean;
  hasThreadsLoadError: boolean;
  hasHistoryLoadError: boolean;
  /** Сообщения текущего треда */
  messages: AgentMessage[];
  /** Флаг загрузки */
  isLoading: boolean;
  /** Флаг отмены запроса */
  isCancelling: boolean;
  /** Значение input */
  inputValue: string;
  /** Текущий тред */
  currentThread: AgentThread | null;
  /** Список всех тредов */
  threads: AgentThread[];
  /** Контент гайда для отображения в панели */
  guideContent: string | null;
  /** Название гайда для отображения в панели */
  guideTitle: string | null;
  /** Контент research context для отображения в панели */
  researchContextContent: string | null;
  /** Название research context для отображения в панели */
  researchContextTitle: string | null;
  /** Контент отчёта/цитаты для панели Report (универсальный просмотр markdown) */
  reportPanelContent: string | null;
  /** Заголовок отчёта в панели Report */
  reportPanelTitle: string | null;
  /** Начальный проект для дропдауна (при открытии из цитаты) */
  reportPanelProjectId: string | null;
  /** Начальный record для дропдауна (при открытии из цитаты) */
  reportPanelRecordId: string | null;
  /** Начальный project report (при открытии project_report цитаты). */
  reportPanelProjectReportId: string | null;
  /** Начальный тип отчёта записи (при открытии из цитаты) */
  reportPanelReportType: import("@/components/features/agent/components/report-panel").RecordReportType | null;
  /** Начальный anchor внутри отчёта (section/utterance). */
  reportPanelAnchor: string | null;
  /** Активная панель */
  activePanel: "guide" | "research_context" | "report" | null;
  /** Установить значение input */
  setInputValue: (value: string) => void;
  /** Отправить сообщение */
  sendMessage: (
    content: string,
    reasoningMode?: "fast" | "thinking",
    forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
    projectIds?: string[],
    documentScope?: DocumentScopePayload,
    interaction?: AgentChatInteraction,
    editOptions?: AgentEditSendOptions
  ) => Promise<void>;
  editMessage: (
    messageId: string,
    content: string,
    reasoningMode?: "fast" | "thinking",
    forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
    projectIds?: string[],
    documentScope?: DocumentScopePayload,
    confirmWorkspaceActions?: boolean,
  ) => Promise<void>;
  /**
   * Отменить текущий запрос с синхронным сбросом локального и shared состояния.
   * Безопасно обрабатывает race conditions через проверку актуального AbortController.
   */
  cancelRequest: () => Promise<void>;
  /** Переключить тред */
  switchThread: (threadId: string) => Promise<void>;
  /** Создать новый тред */
  createNewThread: (name?: string) => Promise<void>;
  /** Переименовать тред */
  renameThread: (threadId: string, newName: string) => Promise<void>;
  /** Удалить тред */
  deleteThread: (threadId: string) => Promise<void>;
  /** Обработка клика по suggestion */
  handleSuggestionClick: (suggestion: AgentSuggestion) => void;
  /** Закрыть панель гайда */
  closeGuidePanel: () => void;
  /** Открыть панель гайда с указанным контентом */
  openGuidePanel: (content: string, title?: string) => void;
  /** Закрыть панель research context */
  closeResearchContextPanel: () => void;
  /** Открыть панель research context с указанным контентом */
  openResearchContextPanel: (content: string, title?: string) => void;
  /** Закрыть панель отчёта/цитаты */
  closeReportPanel: () => void;
  /** Открыть панель отчёта с указанным markdown (для цитат и любых отчётов). options — начальный выбор проекта/record/типа отчёта записи. */
  openReportPanel: (
    content: string,
    title?: string,
    options?: {
      projectId?: string;
      recordId?: string;
      projectReportId?: string;
      reportType?: import("@/components/features/agent/components/report-panel").RecordReportType;
      anchor?: string;
    }
  ) => void;
}

/**
 * Конвертировать DTO сообщения в UI формат
 */
function convertMessageToUI(
  dto: AgentMessageDTO,
  threadState?: Record<string, unknown> | null
): AgentMessage {
  // Проверяем наличие parts в сообщении - это приоритетнее чем thread state
  // Тег должен показываться только у сообщений с соответствующим part или в активном режиме
  
  // Если есть guide part, устанавливаем mode="interview_guide"
  const hasGuide = dto.parts?.some((part) => part.type === "guide");
  const hasResearchContext = dto.parts?.some((part) => part.type === "research_context");
  
  // Если есть question part или confirmation part - это часть процесса создания гайда
  const hasQuestion = dto.parts?.some((part) => part.type === "question");
  const hasConfirmation = dto.parts?.some((part) => part.type === "confirmation");
  
  // Проверяем, является ли вопрос от оркестратора (ID начинается с "supervisor_q_")
  const isOrchestratorQuestion = dto.parts?.some(
    (part) =>
      part.type === "question" &&
      "question" in part &&
      typeof part.question === "object" &&
      part.question !== null &&
      "id" in part.question &&
      typeof part.question.id === "string" &&
      part.question.id.startsWith("supervisor_q_")
  );
  
  // Если есть documentation part, устанавливаем mode="documentation"
  const hasDocumentation = dto.parts?.some(
    (part): part is MessagePart & { type: "documentation" } =>
      part.type === "documentation"
  );
  
  let mode: AgentMode | undefined;
  
  // ВАЖНО: Приоритет сохраненного mode из сообщения (dto.mode) над всеми остальными источниками
  // Это гарантирует, что тег сообщения не будет меняться после того, как сообщение было создано
  if (dto.mode && dto.mode !== "normal") {
    // Используем сохраненный mode из сообщения
    mode = dto.mode as AgentMode;
  } else {
    // Fallback: определяем mode по parts и threadState только если mode не сохранен в сообщении
    // Приоритет: parts в сообщении > thread state для активных режимов
    if (hasGuide) {
      // Финальный этап - гайд сгенерирован
      mode = "interview_guide";
    } else if (hasResearchContext) {
      mode = "research_context";
    } else if (hasQuestion || hasConfirmation) {
      // Промежуточный этап - задаются вопросы для создания гайда или контекста
      // ВАЖНО: Если вопрос от оркестратора (ID начинается с "supervisor_q_"), 
      // не проставляем mode - это сообщение от оркестратора, а не от субагента
      if (isOrchestratorQuestion) {
        // Вопрос от оркестратора - не проставляем mode (остается undefined или "normal")
        mode = undefined;
      } else {
        // Это сообщение от субагента - определяем mode по threadState
        // Если threadState.mode не установлен или равен "normal", это означает что субагент завершил работу,
        // но сообщение было отправлено когда mode был установлен - используем threadState.mode если доступен,
        // иначе пытаемся определить по контексту (проверяем наличие guide_content или research_context_content)
        if (threadState && typeof threadState === "object" && "mode" in threadState) {
          const modeValue = threadState.mode;
          if (modeValue === "research_context") {
            mode = "research_context";
          } else if (modeValue === "interview_guide") {
            mode = "interview_guide";
          } else {
            // threadState.mode = "normal" или не установлен, но есть question/confirmation part
            // Проверяем наличие guide_content или research_context_content для определения типа субагента
            if ("guide_content" in threadState && threadState.guide_content) {
              mode = "interview_guide";
            } else if ("research_context_content" in threadState && threadState.research_context_content) {
              mode = "research_context";
            } else {
              // По умолчанию считаем interview_guide (более частый случай)
              mode = "interview_guide";
            }
          }
        } else {
          // Если threadState не передан, но есть question/confirmation part,
          // это сообщение от субагента - используем interview_guide по умолчанию
          mode = "interview_guide";
        }
      }
    } else if (hasDocumentation) {
      mode = "documentation";
    } else {
      // Для сообщений от ассистента без специальных parts:
      // Если threadState.mode установлен в interview_guide или research_context,
      // это сообщение от субагента - показываем тег
      if (threadState && typeof threadState === "object" && "mode" in threadState) {
        const modeValue = threadState.mode;
        if (modeValue === "research_context") {
          mode = "research_context";
        } else if (modeValue === "interview_guide") {
          mode = "interview_guide";
        } else if (modeValue === "normal") {
          mode = "normal";
        }
      }
    }
  }

  // Если есть guide part, убираем полный markdown из content
  // (он будет показан в GuideCard)
  let content = dto.content;
  if (hasGuide && dto.parts) {
    const guidePart = dto.parts.find((part) => part.type === "guide");
    if (guidePart && guidePart.type === "guide") {
      // Убираем markdown гайда из content, оставляем только текст до гайда
      // Обычно content содержит текст типа "Отлично, у нас есть необходимая информация. Я подготовлю гайд для интервью."
      // и затем markdown гайда. Мы оставляем только первую часть.
      const guideMarkdown = guidePart.guide.markdown;
      if (content.includes(guideMarkdown)) {
        content = content.replace(guideMarkdown, "").trim();
      }
    }
  }

  if (hasResearchContext && dto.parts) {
    const contextPart = dto.parts.find((part) => part.type === "research_context");
    if (contextPart && contextPart.type === "research_context") {
      const contextMarkdown = contextPart.brief.markdown;
      if (content.includes(contextMarkdown)) {
        content = content.replace(contextMarkdown, "").trim();
      }
    }
  }

  return {
    id: dto.id,
    role: dto.message_type,
    content,
    timestamp: new Date(dto.created_at),
    parts: dto.parts || undefined,
    mode,
    llm_model: dto.llm_model,
    canEdit: dto.can_edit ?? false,
  };
}

/**
 * Извлечь guide content из частей сообщения
 */
function extractGuideContent(parts: MessagePart[] | null): string | null {
  if (!parts) return null;

  for (const part of parts) {
    if (part.type === "guide") {
      return part.guide.markdown;
    }
  }

  return null;
}

/**
 * Извлечь guide title из частей сообщения
 */
function extractGuideTitle(parts: MessagePart[] | null): string | null {
  if (!parts) return null;

  for (const part of parts) {
    if (part.type === "guide") {
      return part.guide.title;
    }
  }

  return null;
}

function extractResearchContextContent(parts: MessagePart[] | null): string | null {
  if (!parts) return null;

  for (const part of parts) {
    if (part.type === "research_context") {
      return part.brief.markdown;
    }
  }

  return null;
}

function extractResearchContextTitle(parts: MessagePart[] | null): string | null {
  if (!parts) return null;

  for (const part of parts) {
    if (part.type === "research_context") {
      return part.brief.title;
    }
  }

  return null;
}

const DEFAULT_ACTIVITY_SUMMARY = "Working on your request";

function getInitialActivityState(): AgentMessageActivityState {
  return {
    summary: DEFAULT_ACTIVITY_SUMMARY,
    events: [],
  };
}

function formatToolName(raw: unknown): string {
  if (typeof raw !== "string" || !raw.trim()) {
    return "tool";
  }
  return raw
    .trim()
    .replaceAll("_", " ")
    .replace(/\s+/g, " ");
}

function getEventSummary(event: AgentStreamEvent): string | null {
  switch (event.type) {
    case "status": {
      const title = event.data?.title;
      return typeof title === "string" && title.trim() ? title : "Updating status";
    }
    case "tool_start":
      return `Running ${formatToolName(event.data?.tool_name)}`;
    case "tool_end":
      return `${formatToolName(event.data?.tool_name)} completed`;
    case "tool_error":
      return `${formatToolName(event.data?.tool_name)} failed`;
    case "ask_project_attempt": {
      const attempt = typeof event.data?.attempt === "number" ? event.data.attempt : null;
      const maxAttempts =
        typeof event.data?.max_attempts === "number" ? event.data.max_attempts : null;
      if (attempt && maxAttempts) {
        return `Ask Project attempt ${attempt}/${maxAttempts}`;
      }
      return "Ask Project retrieval attempt";
    }
    case "ask_project_finish":
      return "Finalizing Ask Project answer";
    case "error":
      return "Failed to complete request";
    default:
      return null;
  }
}

function getActivityEvent(
  event: AgentStreamEvent,
  eventIndex: number
): AgentMessageActivityEvent | null {
  const timestamp = typeof event.ts === "string" ? event.ts : new Date().toISOString();
  const id = `${timestamp}-${event.type}-${eventIndex}`;

  switch (event.type) {
    case "status": {
      const title =
        typeof event.data?.title === "string" && event.data.title.trim()
          ? event.data.title
          : "Status update";
      const detail = typeof event.data?.detail === "string" ? event.data.detail : undefined;
      const stateValue = event.data?.state;
      const state: "running" | "done" | "error" =
        stateValue === "done" || stateValue === "error" ? stateValue : "running";
      return { id, type: "status", title, detail, timestamp, state };
    }
    case "tool_start":
      return {
        id,
        type: "tool_start",
        title: `Started ${formatToolName(event.data?.tool_name)}`,
        detail: typeof event.data?.source === "string" ? event.data.source : "orchestrator",
        timestamp,
        state: "running",
      };
    case "tool_end": {
      const outcome = typeof event.data?.outcome === "string" ? event.data.outcome : "ok";
      return {
        id,
        type: "tool_end",
        title: `Finished ${formatToolName(event.data?.tool_name)}`,
        detail: `Outcome: ${outcome}`,
        timestamp,
        state: outcome === "error" ? "error" : "done",
      };
    }
    case "tool_error":
      return {
        id,
        type: "tool_error",
        title: `Error in ${formatToolName(event.data?.tool_name)}`,
        detail:
          typeof event.data?.message === "string" ? event.data.message : "Tool execution failed.",
        timestamp,
        state: "error",
      };
    case "ask_project_attempt": {
      const attempt = typeof event.data?.attempt === "number" ? event.data.attempt : "?";
      const maxAttempts =
        typeof event.data?.max_attempts === "number" ? event.data.max_attempts : "?";
      const status = typeof event.data?.status === "string" ? event.data.status : "unknown";
      const sourcesCount =
        typeof event.data?.sources_count === "number" ? event.data.sources_count : 0;
      const inlineCount =
        typeof event.data?.inline_count === "number" ? event.data.inline_count : 0;
      return {
        id,
        type: "ask_project_attempt",
        title: `Ask Project attempt ${attempt}/${maxAttempts}`,
        detail: `status=${status}, sources=${sourcesCount}, inline=${inlineCount}`,
        timestamp,
        state: status === "error" ? "error" : "running",
      };
    }
    case "ask_project_finish": {
      const attempts = typeof event.data?.attempts === "number" ? event.data.attempts : 0;
      const sourcesCount =
        typeof event.data?.sources_count === "number" ? event.data.sources_count : 0;
      const inlineCount =
        typeof event.data?.inline_count === "number" ? event.data.inline_count : 0;
      const finishReason =
        typeof event.data?.finish_reason === "string" ? event.data.finish_reason : "completed";
      return {
        id,
        type: "ask_project_finish",
        title: "Ask Project retrieval finished",
        detail: `attempts=${attempts}, sources=${sourcesCount}, inline=${inlineCount}, reason=${finishReason}`,
        timestamp,
        state: "done",
      };
    }
    case "error":
      return {
        id,
        type: "error",
        title: "Request failed",
        detail:
          typeof event.data?.message === "string"
            ? event.data.message
            : "Unknown streaming error.",
        timestamp,
        state: "error",
      };
    default:
      return null;
  }
}

export function useAgentChat(
  options: UseAgentChatOptions = {}
): UseAgentChatReturn {
  const { onThreadChange, onThreadsUpdate } = options;
  const { refreshBalance } = useBalance();
  const projectsStore = useProjectsRootStore();
  const recordsStore = useRecordsRootStore();
  
  // Стабилизируем колбэки через useRef, чтобы они не вызывали перезапуск useEffect
  const onThreadChangeRef = useRef(onThreadChange);
  const onThreadsUpdateRef = useRef(onThreadsUpdate);
  
  // Обновляем refs при изменении колбэков
  useEffect(() => {
    onThreadChangeRef.current = onThreadChange;
    onThreadsUpdateRef.current = onThreadsUpdate;
  }, [onThreadChange, onThreadsUpdate]);

  // State
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [currentThread, setCurrentThread] = useState<AgentThread | null>(null);
  const [threads, setThreads] = useState<AgentThread[]>([]);
  const [guideContent, setGuideContent] = useState<string | null>(null);
  const [guideTitle, setGuideTitle] = useState<string | null>(null);
  const [researchContextContent, setResearchContextContent] = useState<string | null>(null);
  const [researchContextTitle, setResearchContextTitle] = useState<string | null>(null);
  const [reportPanelContent, setReportPanelContent] = useState<string | null>(null);
  const [reportPanelTitle, setReportPanelTitle] = useState<string | null>(null);
  const [reportPanelProjectId, setReportPanelProjectId] = useState<string | null>(null);
  const [reportPanelRecordId, setReportPanelRecordId] = useState<string | null>(null);
  const [reportPanelProjectReportId, setReportPanelProjectReportId] = useState<string | null>(null);
  const [reportPanelReportType, setReportPanelReportType] = useState<
    import("@/components/features/agent/components/report-panel").RecordReportType | null
  >(null);
  const [reportPanelAnchor, setReportPanelAnchor] = useState<string | null>(null);
  const [activePanel, setActivePanel] = useState<"guide" | "research_context" | "report" | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasThreadsLoadError, setHasThreadsLoadError] = useState(false);
  const [hasHistoryLoadError, setHasHistoryLoadError] = useState(false);

  // Refs
  const currentThreadIdRef = useRef<string | null>(null);
  const guideContentRef = useRef<string | null>(null);
  const guideTitleRef = useRef<string | null>(null);
  const researchContextContentRef = useRef<string | null>(null);
  const researchContextTitleRef = useRef<string | null>(null);
  const loadThreadHistoryRef = useRef<((threadId: string) => Promise<void>) | null>(null);
  // Флаг для отслеживания, был ли гайд уже показан пользователю
  // Если пользователь закрыл панель, не открываем её автоматически снова
  const hasGuideBeenShownRef = useRef<boolean>(false);
  const hasResearchContextBeenShownRef = useRef<boolean>(false);
  // Используем контекст для shared refs вместо window объекта
  // Это обеспечивает правильную изоляцию и поддержку SSR
  const {
    sharedIsLoadingRef,
    sharedIsCancellingRef,
    sharedAbortControllerRef,
  } = useAgentChatCancellationContext();

  // Локальные refs для fallback (используются только если контекст недоступен)
  const abortControllerRef = useRef<AbortController | null>(null);
  const isCancellingRef = useRef<boolean>(false);
  const isLoadingRef = useRef<boolean>(false);
  const activeTurnIdRef = useRef<string | null>(null);
  const activeAssistantMessageIdRef = useRef<string | null>(null);
  const cancelledTurnIdsRef = useRef<Set<string>>(new Set());
  const activeRestoreAttemptedRef = useRef(false);

  /**
   * Загрузить историю треда
   */
  const loadThreadHistory = useCallback(async (threadId: string) => {
    setHasHistoryLoadError(false);
    try {
      // Получаем thread для доступа к state
      const thread = await agentService.getThread(threadId);
      
      // Verify thread exists and is not deleted
      if (!thread || thread.is_deleted) {
        setHasHistoryLoadError(true);
        console.warn(`Thread ${threadId} not found or deleted`);
        setMessages([]);
        return;
      }
      
      const history = await agentService.getHistory(threadId);
      const uiMessages = history.map((msg) => convertMessageToUI(msg, thread.state));
      setMessages(uiMessages);

      // Не открываем панель гайда автоматически при загрузке треда
      // Панель должна открываться только при генерации нового гайда
      guideContentRef.current = null;
      guideTitleRef.current = null;
      researchContextContentRef.current = null;
      researchContextTitleRef.current = null;
      setGuideContent(null);
      setGuideTitle(null);
      setResearchContextContent(null);
      setResearchContextTitle(null);
      setReportPanelContent(null);
      setReportPanelTitle(null);
      setReportPanelProjectId(null);
      setReportPanelRecordId(null);
      setReportPanelProjectReportId(null);
      setReportPanelReportType(null);
      setReportPanelAnchor(null);
      setActivePanel(null);
      hasGuideBeenShownRef.current = false;
      hasResearchContextBeenShownRef.current = false;
    } catch (error) {
      setHasHistoryLoadError(true);
      console.error("Failed to load history:", error);
      // Don't clear messages on error - preserve what we have
      // Only clear if thread truly doesn't exist
      if (error instanceof Error && error.message.includes("not found")) {
        setMessages([]);
      }
    }
  }, []);
  
  // Сохраняем функцию в ref для использования в useEffect
  loadThreadHistoryRef.current = loadThreadHistory;

  // Флаг для предотвращения параллельных загрузок
  const isLoadingThreadsRef = useRef(false);

  // Load threads on mount
  useEffect(() => {
    const loadThreads = async () => {
      // Предотвращаем параллельные загрузки
      if (isLoadingThreadsRef.current) {
        return;
      }

      if (isInitialized) {
        return;
      }

      isLoadingThreadsRef.current = true;

      try {
        const loadedThreads = await agentService.getThreads();
        
        // Обновляем threads только если данные действительно изменились
        // Используем функциональную форму setThreads для доступа к актуальному значению
        setThreads((currentThreads) => {
          // Сравниваем по ID, чтобы избежать лишних обновлений
          const threadsChanged = 
            currentThreads.length !== loadedThreads.length ||
            currentThreads.some((t, i) => t.id !== loadedThreads[i]?.id);

          return threadsChanged ? loadedThreads : currentThreads; // Возвращаем тот же массив, чтобы не вызывать перерендер
        });

        if (onThreadsUpdateRef.current) {
          onThreadsUpdateRef.current(loadedThreads);
        }

        // Всегда открываем раздел в пустом состоянии, без авто-выбора последнего треда.
        // Список тредов остается доступным в dropdown, но текущий тред не выбран.
        setCurrentThread(null);
        currentThreadIdRef.current = null;
        setMessages([]);
        guideContentRef.current = null;
        guideTitleRef.current = null;
        researchContextContentRef.current = null;
        researchContextTitleRef.current = null;
        setGuideContent(null);
        setGuideTitle(null);
        setResearchContextContent(null);
        setResearchContextTitle(null);
        setReportPanelContent(null);
        setReportPanelTitle(null);
        setReportPanelProjectId(null);
        setReportPanelRecordId(null);
        setReportPanelProjectReportId(null);
        setReportPanelReportType(null);
        setReportPanelAnchor(null);
        setActivePanel(null);
        hasGuideBeenShownRef.current = false;
        hasResearchContextBeenShownRef.current = false;
      } catch (error) {
        setHasThreadsLoadError(true);
        console.error("[use-agent-chat] Failed to load threads:", error);
      } finally {
        isLoadingThreadsRef.current = false;
        setIsInitialized(true);
      }
    };

    loadThreads();
    // Запускаем только один раз при монтировании
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // УБРАНО: useEffect синхронизации ref с isLoading
  // Проблема: useEffect срабатывает асинхронно и может перезаписывать ref, установленный синхронно
  // Решение: полагаемся только на синхронные обновления ref в нужных местах (sendMessage, switchThread, cancelRequest)
  // useEffect(() => {
  //   isLoadingRef.current = isLoading;
  // }, [isLoading]);

  /**
   * Переключить на другой тред
   */
  const switchThread = useCallback(
    async (threadId: string) => {
      if (threadId === currentThreadIdRef.current) {
        return;
      }

      // Отменяем текущий запрос если он есть
      const currentController =
        sharedAbortControllerRef.current ?? abortControllerRef.current;
      if (currentController) {
        currentController.abort();
        if (sharedAbortControllerRef.current === currentController) {
          sharedAbortControllerRef.current = null;
        }
        if (abortControllerRef.current === currentController) {
          abortControllerRef.current = null;
        }
      }

      // Обновляем ref синхронно перед setState для решения проблемы замыкания
      isLoadingRef.current = true;
      sharedIsLoadingRef.current = true;
      setIsLoading(true);
      guideContentRef.current = null;
      guideTitleRef.current = null;
      researchContextContentRef.current = null;
      researchContextTitleRef.current = null;
      setGuideContent(null);
      setGuideTitle(null);
      setResearchContextContent(null);
      setResearchContextTitle(null);
      setReportPanelContent(null);
      setReportPanelTitle(null);
      setReportPanelProjectId(null);
      setReportPanelRecordId(null);
      setReportPanelProjectReportId(null);
      setReportPanelReportType(null);
      setReportPanelAnchor(null);
      setActivePanel(null);
      hasGuideBeenShownRef.current = false;
      hasResearchContextBeenShownRef.current = false;

      try {
        const thread = await agentService.getThread(threadId);
        setCurrentThread(thread);
        currentThreadIdRef.current = threadId;

        await loadThreadHistory(threadId);

        // Не открываем панель гайда автоматически при переключении треда
        // Панель должна открываться только при генерации нового гайда

        if (onThreadChangeRef.current) {
          onThreadChangeRef.current(threadId);
        }
      } catch (error) {
        setHasHistoryLoadError(true);
        console.error("[use-agent-chat] Failed to switch thread:", error);
        toast.error("Failed to load thread");
      } finally {
        // Обновляем ref синхронно перед setState
        isLoadingRef.current = false;
        sharedIsLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [loadThreadHistory, sharedAbortControllerRef, sharedIsLoadingRef]
  );

  /**
   * Создать новый тред
   */
  const createNewThread = useCallback(
    async (name?: string) => {
      try {
        // Ensure name is a string or undefined, not an event object
        const threadName = typeof name === "string" ? name : undefined;
        const newThread = await agentService.createThread(threadName);

        // Update threads list
        const updatedThreads = [newThread, ...threads];
        setThreads(updatedThreads);

        if (onThreadsUpdateRef.current) {
          onThreadsUpdateRef.current(updatedThreads);
        }

        // Switch to new thread
        setCurrentThread(newThread);
        currentThreadIdRef.current = newThread.id;
        setHasHistoryLoadError(false);
        setMessages([]);
        guideContentRef.current = null;
        guideTitleRef.current = null;
        researchContextContentRef.current = null;
        researchContextTitleRef.current = null;
        setGuideContent(null);
        setGuideTitle(null);
        setResearchContextContent(null);
        setResearchContextTitle(null);
        setReportPanelContent(null);
        setReportPanelTitle(null);
        setReportPanelProjectId(null);
        setReportPanelRecordId(null);
        setReportPanelProjectReportId(null);
        setReportPanelReportType(null);
        setReportPanelAnchor(null);
        setActivePanel(null);
        hasGuideBeenShownRef.current = false;
        hasResearchContextBeenShownRef.current = false;

        if (onThreadChangeRef.current) {
          onThreadChangeRef.current(newThread.id);
        }
      } catch (error) {
        console.error("Failed to create thread:", error);
        toast.error("Failed to create new thread");
      }
    },
    [threads]
  );

  /**
   * Переименовать тред
   */
  const renameThread = useCallback(
    async (threadId: string, newName: string) => {
      try {
        const updatedThread = await agentService.renameThread(threadId, newName);

        // Update threads list
        const updatedThreads = threads.map((t) =>
          t.id === threadId ? updatedThread : t
        );
        setThreads(updatedThreads);

        if (onThreadsUpdateRef.current) {
          onThreadsUpdateRef.current(updatedThreads);
        }

        // Update current thread if it's the renamed one
        if (threadId === currentThreadIdRef.current) {
          setCurrentThread(updatedThread);
        }

        toast.success("Thread renamed");
      } catch (error) {
        console.error("Failed to rename thread:", error);
        toast.error("Failed to rename thread");
        throw error; // Re-throw to let modal handle it
      }
    },
    [threads]
  );

  /**
   * Удалить тред
   */
  const deleteThread = useCallback(
    async (threadId: string) => {
      try {
        await agentService.deleteThread(threadId);

        // Update threads list
        const updatedThreads = threads.filter((t) => t.id !== threadId);
        setThreads(updatedThreads);

        if (onThreadsUpdateRef.current) {
          onThreadsUpdateRef.current(updatedThreads);
        }

        // If deleted current thread, switch to another or clear
        if (threadId === currentThreadIdRef.current) {
          if (updatedThreads.length > 0) {
            await switchThread(updatedThreads[0].id);
          } else {
            setCurrentThread(null);
            currentThreadIdRef.current = null;
            setMessages([]);
            setGuideContent(null);
            setGuideTitle(null);
            setResearchContextContent(null);
            setResearchContextTitle(null);
            setReportPanelContent(null);
            setReportPanelTitle(null);
            setReportPanelProjectId(null);
            setReportPanelRecordId(null);
            setReportPanelProjectReportId(null);
            setReportPanelReportType(null);
            setReportPanelAnchor(null);
            guideTitleRef.current = null;
            researchContextTitleRef.current = null;
            setActivePanel(null);
            hasGuideBeenShownRef.current = false;
            hasResearchContextBeenShownRef.current = false;
          }
        }

        toast.success("Thread deleted");
      } catch (error) {
        console.error("Failed to delete thread:", error);
        toast.error("Failed to delete thread");
      }
    },
    [threads, switchThread]
  );

  /**
   * Отменить текущий запрос
   * 
   * Использует shared refs для синхронной проверки состояния между экземплярами хука,
   * что решает проблему замыкания при асинхронных операциях.
   * 
   * Обработка race conditions:
   * - Сохраняет ссылку на текущий AbortController перед отменой
   * - Очищает refs только если они все еще указывают на тот же controller
   * - Это предотвращает конфликты при одновременной отмене и завершении запроса
   * 
   * @throws {Error} Логирует ошибки, но не выбрасывает их пользователю
   */
  const cancelRequest = useCallback(async () => {
    // Предотвращаем множественные вызовы - используем shared ref для синхронной проверки
    if (sharedIsCancellingRef.current) {
      return;
    }

    // Проверяем isLoading через ref для синхронного доступа (решает проблему замыкания)
    // ВАЖНО: используем sharedIsLoadingRef для доступа между экземплярами хука
    if (!sharedIsLoadingRef.current) {
      return;
    }

    const threadId = currentThreadIdRef.current;
    if (!threadId) {
      return;
    }

    // Устанавливаем флаг отмены синхронно через ref и state
    // Это обеспечивает немедленное обновление UI и предотвращает повторные вызовы
    isCancellingRef.current = true;
    sharedIsCancellingRef.current = true;
    setIsCancelling(true);

    try {
      // Server cancellation is authoritative. AbortController only closes the SSE subscription.
      const currentController = sharedAbortControllerRef.current;
      const turnId = activeTurnIdRef.current;
      const cancelled = await agentService.cancelChat(threadId, turnId ?? undefined);
      if (!cancelled) {
        throw new Error("Server did not confirm turn cancellation");
      }

      if (turnId) {
        cancelledTurnIdsRef.current.add(turnId);
      }

      const assistantMessageId = activeAssistantMessageIdRef.current;
      if (assistantMessageId) {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === assistantMessageId
              ? {
                  ...message,
                  isStreaming: false,
                  turnStatus: "cancelled",
                  activity: undefined,
                }
              : message
          )
        );
      }

      if (currentController) {
        currentController.abort();
        if (sharedAbortControllerRef.current === currentController) {
          sharedAbortControllerRef.current = null;
        }
        if (abortControllerRef.current === currentController) {
          abortControllerRef.current = null;
        }
      }

      // Останавливаем загрузку и сбрасываем флаг отмены синхронно
      // Это предотвращает задержки и race conditions
      sharedIsLoadingRef.current = false;
      isLoadingRef.current = false;
      isCancellingRef.current = false;
      sharedIsCancellingRef.current = false;
      setIsLoading(false);
      setIsCancelling(false);
      activeTurnIdRef.current = null;
      activeAssistantMessageIdRef.current = null;
      trackResult(ANALYTICS_EVENTS.RESULT_AGENT_QUERY_CANCELLED, {
        thread_id: threadId,
        source: "cancel_request",
      });
    } catch (error) {
      console.error("Error in cancelRequest:", error);
      isCancellingRef.current = false;
      sharedIsCancellingRef.current = false;
      setIsCancelling(false);
      toast.error("Failed to stop the running turn");
    }
  }, [sharedAbortControllerRef, sharedIsCancellingRef, sharedIsLoadingRef]);

  /**
   * Отправить сообщение
   * 
   * Синхронизация состояния:
   * - Обновляет refs синхронно перед setState для решения проблемы замыкания
   * - Использует shared refs для доступа между экземплярами хука
   * 
   * Обработка отмены:
   * - Отменяет предыдущий запрос перед созданием нового
   * - Создает новый AbortController для каждого запроса
   * - Проверяет abortSignal в процессе стриминга
   * - Удаляет пустые сообщения при отмене
   * 
   * Обработка race conditions:
   * - В finally блоке проверяет, что очищаемый controller все еще текущий
   * - Это предотвращает очистку нового controller при одновременной отмене и завершении
   * 
   * @param content - Текст сообщения
   * @param reasoningMode - Режим reasoning (fast или thinking)
   * @param forcedAction - Принудительный выбор инструмента/агента
   */
  const sendMessage = useCallback(
    async (
      content: string,
      reasoningMode: "fast" | "thinking" = "fast",
      forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
      projectIds?: string[],
      documentScope?: DocumentScopePayload,
      interaction?: AgentChatInteraction,
      editOptions?: AgentEditSendOptions
    ) => {
      if (
        !content.trim() ||
        isLoadingRef.current ||
        sharedIsLoadingRef.current ||
        activeTurnIdRef.current
      ) {
        return;
      }

      const editTargetIndex = editOptions?.editOfMessageId
        ? messages.findIndex((message) => message.id === editOptions.editOfMessageId)
        : -1;
      if (editOptions?.editOfMessageId && (!currentThreadIdRef.current || editTargetIndex < 0)) {
        toast.error(
          !currentThreadIdRef.current
            ? "Select a thread before editing a message"
            : "This message is no longer available for editing",
        );
        return;
      }

      // Отменяем предыдущий запрос, если он есть
      // Это важно для предотвращения конфликтов при быстрой отправке нескольких сообщений
      if (sharedAbortControllerRef.current) {
        sharedAbortControllerRef.current.abort();
        sharedAbortControllerRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Создаем новый AbortController для этого запроса
      // Сохраняем в локальный и shared ref для доступа из разных мест
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      sharedAbortControllerRef.current = abortController; // Также сохраняем в shared ref
      const turnId = crypto.randomUUID();
      activeTurnIdRef.current = turnId;

      // Create thread if none exists
      let threadId = currentThreadIdRef.current;

      if (!threadId) {
        try {
          const newThread = await agentService.createThread();
          threadId = newThread.id;

          const updatedThreads = [newThread, ...threads];
          setThreads(updatedThreads);
          setCurrentThread(newThread);
          currentThreadIdRef.current = threadId;

          if (onThreadsUpdateRef.current) {
            onThreadsUpdateRef.current(updatedThreads);
          }

          if (onThreadChangeRef.current) {
            onThreadChangeRef.current(threadId);
          }
        } catch (error) {
          console.error("Failed to create thread:", error);
          toast.error("Failed to start conversation");
          return;
        }
      }

      // Add user message to UI (or replace the edited message and truncate its descendants).
      const userMessage: AgentMessage = editOptions?.editOfMessageId
        ? {
            ...messages[editTargetIndex],
            content: content.trim(),
            canEdit: false,
            timestamp: new Date(),
          }
        : {
            id: `user-${Date.now()}`,
            role: "user",
            content: content.trim(),
            timestamp: new Date(),
          };

      trackUi(ANALYTICS_EVENTS.UI_AGENT_QUERY_SENT, {
        reasoning_mode: reasoningMode,
        forced_action: forcedAction ?? "none",
        selected_projects_count: projectIds?.length ?? 0,
        has_document_scope: Boolean(documentScope),
        content_length: content.trim().length,
        has_existing_thread: Boolean(currentThreadIdRef.current),
      });

      setMessages((prev) =>
        editOptions?.editOfMessageId && editTargetIndex >= 0
          ? [...prev.slice(0, editTargetIndex), userMessage]
          : [...prev, userMessage]
      );
      setInputValue("");
      // Обновляем ref синхронно перед setState для решения проблемы замыкания
      isLoadingRef.current = true;
      sharedIsLoadingRef.current = true;
      setIsLoading(true);
      // Сбрасываем флаг отмены при новом запросе (синхронно через ref и state)
      isCancellingRef.current = false;
      sharedIsCancellingRef.current = false;
      setIsCancelling(false);

      // Create assistant message placeholder
      const assistantMessageId = `assistant-${Date.now()}`;
      const streamEventsEnabled = isAgentStreamEventsEnabled();
      const assistantMessage: AgentMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
        isStreaming: true,
        turnStatus: "running",
      };
      activeAssistantMessageIdRef.current = assistantMessageId;

      setMessages((prev) => [...prev, assistantMessage]);

      try {
        let accumulatedContent = "";
        const handleStreamEvent = streamEventsEnabled
          ? (event: AgentStreamEvent) => {
              if (abortController.signal.aborted) {
                return;
              }

              if (event.type === "done" || event.type === "text_delta") {
                return;
              }
              if (event.type === "heartbeat") {
                return;
              }

              setMessages((prev) =>
                prev.map((msg) => {
                  if (msg.id !== assistantMessageId) {
                    return msg;
                  }

                  const currentActivity = msg.activity ?? getInitialActivityState();
                  const nextSummary = getEventSummary(event) ?? currentActivity.summary;
                  const nextEvent = getActivityEvent(event, currentActivity.events.length);
                  const nextEvents = nextEvent
                    ? [...currentActivity.events, nextEvent]
                    : currentActivity.events;

                  return {
                    ...msg,
                    activity: {
                      summary: nextSummary,
                      events: nextEvents,
                    },
                  };
                })
              );
            }
          : undefined;

        let terminalStatus: "completed" | "failed" | "cancelled" | "superseded" | null = null;
        let reconnectAttempt = 0;
        while (!abortController.signal.aborted && terminalStatus === null) {
          if (reconnectAttempt > 0) {
            accumulatedContent = "";
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === assistantMessageId ? { ...msg, content: "" } : msg
              )
            );
          }
          try {
            const streamStatus = await agentService.streamMessage(
              threadId,
              content.trim(),
              (chunk: string) => {
                if (abortController.signal.aborted) {
                  return;
                }
                accumulatedContent += chunk;
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === assistantMessageId
                      ? {
                          ...msg,
                          content: accumulatedContent,
                          isStreaming: true,
                          turnStatus: "running",
                        }
                      : msg
                  )
                );
              },
              reasoningMode,
              forcedAction,
              projectIds,
              documentScope,
              abortController.signal,
              handleStreamEvent,
              interaction,
              turnId,
              editOptions?.editOfMessageId,
              editOptions?.confirmWorkspaceActions ?? false,
              editOptions?.editOfMessageId ? "/agent/chat/edit" : "/agent/chat",
            );
            terminalStatus =
              streamStatus === "running" || streamStatus === "superseded"
                ? streamStatus === "superseded" ? "failed" : null
                : streamStatus;
          } catch (error) {
            if (abortController.signal.aborted) {
              break;
            }
            const requestError = error as { name?: string; status?: number };
            if (
              editOptions?.editOfMessageId &&
              (requestError.name === "AgentChatRequestError" ||
                requestError.name === "AgentChatTurnError" ||
                (typeof requestError.status === "number" && requestError.status >= 400 && requestError.status < 500))
            ) {
              throw error;
            }
            try {
              const serverTurn = await agentService.getTurn(turnId);
              if (serverTurn.status !== "running") {
                terminalStatus = serverTurn.status;
                break;
              }
            } catch {
              // Network state is unknown; keep the turn active and retry the subscription.
            }
            reconnectAttempt += 1;
            await new Promise((resolve) =>
              setTimeout(resolve, Math.min(1000 * reconnectAttempt, 5000))
            );
            continue;
          }

          if (terminalStatus === null && !abortController.signal.aborted) {
            try {
              const serverTurn = await agentService.getTurn(turnId);
              terminalStatus =
                serverTurn.status === "running" || serverTurn.status === "superseded"
                  ? serverTurn.status === "superseded" ? "failed" : null
                  : serverTurn.status;
            } catch {
              terminalStatus = null;
            }
            if (terminalStatus === null) {
              reconnectAttempt += 1;
              await new Promise((resolve) =>
                setTimeout(resolve, Math.min(1000 * reconnectAttempt, 5000))
              );
            }
          }
        }

        if (abortController.signal.aborted) {
          return;
        }
        if (terminalStatus === "cancelled") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === assistantMessageId
                ? { ...msg, isStreaming: false, turnStatus: "cancelled", activity: undefined }
                : msg
            )
          );
          return;
        }
        if (terminalStatus === "failed" || terminalStatus === "superseded") {
          throw new Error("Agent turn failed");
        }

        // Mark streaming as complete
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, isStreaming: false, turnStatus: "completed", activity: undefined }
              : msg
          )
        );

        trackResult(ANALYTICS_EVENTS.RESULT_AGENT_QUERY_SUCCESS, {
          thread_id: threadId,
          forced_action: forcedAction ?? "none",
          reasoning_mode: reasoningMode,
          selected_projects_count: projectIds?.length ?? 0,
          response_length: accumulatedContent.length,
        });

        // Обновляем баланс после успешного ответа агента (токены были списаны на бэкенде)
        try {
          await refreshBalance();
        } catch (balanceError) {
          // Не прерываем выполнение, если обновление баланса не удалось
          console.error("Failed to refresh balance after message:", balanceError);
        }

        // Reload history to get updated messages with parts
        if (threadId) {
          try {
            // Small delay to ensure backend has saved the message
            await new Promise((resolve) => setTimeout(resolve, 500));
            
            // Get updated thread to access state
            const updatedThread = await agentService.getThread(threadId);
            
            // Verify thread still exists and is not deleted
            if (!updatedThread || updatedThread.is_deleted) {
              console.warn("Thread not found or deleted after message send");
              // Don't clear current thread, just log the warning
              return;
            }
            
            const history = await agentService.getHistory(threadId);
            const uiMessages = history.map((msg) => convertMessageToUI(msg, updatedThread.state));
            setMessages(uiMessages);

            refreshStoresForWorkspaceActions(uiMessages, {
              loadProjects: (force) => projectsStore.loadProjects(force),
              invalidateRecords: () => recordsStore.invalidateRecords(),
            });
            
            // Update current thread - preserve it even if update fails partially
            setCurrentThread(updatedThread);
            currentThreadIdRef.current = threadId;
            
            // Update threads list to keep it in sync
            setThreads((prev) => {
              const existing = prev.find((t) => t.id === threadId);
              if (existing) {
                // Update existing thread
                return prev.map((t) => (t.id === threadId ? updatedThread : t));
              } else {
                // Thread not in list, add it
                return [updatedThread, ...prev];
              }
            });
            
            // Check for guide in messages
            let foundGuide: string | null = null;
            let foundGuideTitle: string | null = null;
            for (const msg of history) {
              const guide = extractGuideContent(msg.parts);
              const guideTitle = extractGuideTitle(msg.parts);
              if (guide) {
                foundGuide = guide;
                if (guideTitle) {
                  foundGuideTitle = guideTitle;
                }
                break;
              }
            }
            
            // Check for research context in messages
            let foundResearchContext: string | null = null;
            let foundResearchContextTitle: string | null = null;
            for (const msg of history) {
              const context = extractResearchContextContent(msg.parts);
              const contextTitle = extractResearchContextTitle(msg.parts);
              if (context) {
                foundResearchContext = context;
                if (contextTitle) {
                  foundResearchContextTitle = contextTitle;
                }
                break;
              }
            }

            const threadState = updatedThread.state as Record<string, unknown> | null;
            const stateContext = threadState && typeof threadState === "object"
              ? (threadState as Record<string, unknown>).research_context_content
              : null;
            const finalResearchContext =
              typeof stateContext === "string" ? stateContext : foundResearchContext;

            // Also check thread for guide content (приоритет у thread.guide_content)
            const finalGuide = updatedThread.guide_content || foundGuide;
            
            // Обновляем только если значение изменилось
            if (finalGuide !== guideContentRef.current) {
              const previousGuide = guideContentRef.current;
              guideContentRef.current = finalGuide;
              
              // Обновляем title, если он найден
              if (foundGuideTitle && foundGuideTitle !== guideTitleRef.current) {
                guideTitleRef.current = foundGuideTitle;
                setGuideTitle(foundGuideTitle);
              }
              
              // Открываем панель только если это новый гайд (переход с null на не-null)
              // и гайд ещё не был показан пользователю
              if (finalGuide && !previousGuide && !hasGuideBeenShownRef.current) {
                // Новый гайд создан - открываем панель
                setGuideContent(finalGuide);
                setActivePanel("guide");
                hasGuideBeenShownRef.current = true;
              } else if (!finalGuide) {
                // Гайд удалён или отсутствует - закрываем панель и сбрасываем флаг
                setGuideContent(null);
                setGuideTitle(null);
                guideTitleRef.current = null;
                setActivePanel((current) => (current === "guide" ? null : current));
                hasGuideBeenShownRef.current = false;
              }
              // Если finalGuide есть, но previousGuide тоже был (гайд обновлён),
              // или если гайд уже был показан (hasGuideBeenShownRef.current === true),
              // не открываем панель автоматически
            }

            if (finalResearchContext !== researchContextContentRef.current) {
              const previousContext = researchContextContentRef.current;
              researchContextContentRef.current = finalResearchContext;

              if (foundResearchContextTitle && foundResearchContextTitle !== researchContextTitleRef.current) {
                researchContextTitleRef.current = foundResearchContextTitle;
                setResearchContextTitle(foundResearchContextTitle);
              }

              if (finalResearchContext && !previousContext && !hasResearchContextBeenShownRef.current) {
                setResearchContextContent(finalResearchContext);
                setActivePanel("research_context");
                hasResearchContextBeenShownRef.current = true;
              } else if (!finalResearchContext) {
                setResearchContextContent(null);
                setResearchContextTitle(null);
                researchContextTitleRef.current = null;
                setActivePanel((current) => (current === "research_context" ? null : current));
                hasResearchContextBeenShownRef.current = false;
              }
            }
          } catch (e) {
            console.error("Failed to reload history:", e);
            // Don't clear current thread on error - preserve user's context
            // Just log the error, message is already displayed
          }
        }
      } catch (error) {
        // Если запрос был отменен, не показываем ошибку
        if (
          abortController.signal.aborted ||
          (error instanceof Error && error.name === "AbortError")
        ) {
          const explicitlyCancelled = cancelledTurnIdsRef.current.has(turnId);
          setMessages((prev) =>
            prev.flatMap((msg) => {
              if (msg.id !== assistantMessageId) {
                return [msg];
              }
              if (explicitlyCancelled) {
                return [{ ...msg, isStreaming: false, turnStatus: "cancelled", activity: undefined }];
              }
              // A technical subscription abort (for example while switching
              // threads) must not invent a server cancellation state.
              return msg.content.trim().length > 0
                ? [{ ...msg, isStreaming: false, activity: undefined }]
                : [];
            })
          );
          trackResult(ANALYTICS_EVENTS.RESULT_AGENT_QUERY_CANCELLED, {
            thread_id: threadId,
            forced_action: forcedAction ?? "none",
            source: "abort_signal",
          });
          // Если сообщение было удалено полностью, это нормально - пользователь отменил до получения ответа
          return;
        }

        console.error("Error sending message:", error);
        trackResult(ANALYTICS_EVENTS.RESULT_AGENT_QUERY_FAILED, {
          thread_id: threadId,
          forced_action: forcedAction ?? "none",
          reasoning_mode: reasoningMode,
          error_code: error instanceof Error ? error.name : "agent_query_failed",
        });

        // Remove failed assistant message
        setMessages((prev) =>
          prev.filter((msg) => msg.id !== assistantMessageId)
        );

        if (editOptions?.editOfMessageId) {
          // The edit transaction is committed before the LLM starts. Reload
          // the branch so a failed turn cannot leave an optimistic message
          // or descendants that no longer exist on the server.
          try {
            await loadThreadHistory(threadId);
          } catch (reloadError) {
            console.error("Failed to reload history after message edit error:", reloadError);
          }
          throw error;
        }
        toast.error("Failed to send message. Please try again.");
      } finally {
        // Очищаем AbortController только если это был текущий запрос
        // Это критически важно для предотвращения race conditions:
        // - Если cancelRequest был вызван одновременно, он мог создать новый controller
        // - Мы не должны очищать новый controller, созданный для следующего запроса
        // - Проверка через === гарантирует, что мы очищаем только свой controller
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        if (sharedAbortControllerRef.current === abortController) {
          sharedAbortControllerRef.current = null;
        }
        if (activeTurnIdRef.current === turnId) {
          activeTurnIdRef.current = null;
        }
        if (activeAssistantMessageIdRef.current === assistantMessageId) {
          activeAssistantMessageIdRef.current = null;
        }
        cancelledTurnIdsRef.current.delete(turnId);
        // Обновляем ref синхронно перед setState для решения проблемы замыкания
        isLoadingRef.current = false;
        sharedIsLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [
      messages,
      threads,
      refreshBalance,
      projectsStore,
      recordsStore,
      loadThreadHistory,
      sharedAbortControllerRef,
      sharedIsCancellingRef,
      sharedIsLoadingRef,
    ]
  );

  const editMessage = useCallback(
    async (
      messageId: string,
      content: string,
      reasoningMode: "fast" | "thinking" = "fast",
      forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
      projectIds?: string[],
      documentScope?: DocumentScopePayload,
      confirmWorkspaceActions = false,
    ) => {
      return sendMessage(
        content,
        reasoningMode,
        forcedAction,
        projectIds,
        documentScope,
        undefined,
        { editOfMessageId: messageId, confirmWorkspaceActions },
      );
    },
    [sendMessage],
  );

  // Restore a server-side running turn after reload. Closing this subscription
  // never calls /cancel; only the explicit Stop action cancels computation.
  useEffect(() => {
    if (!isInitialized || activeRestoreAttemptedRef.current) {
      return;
    }
    activeRestoreAttemptedRef.current = true;
    isLoadingRef.current = true;
    sharedIsLoadingRef.current = true;
    let disposed = false;
    let restoreController: AbortController | null = null;
    let restoredTurnId: string | null = null;

    const restoreActiveTurn = async () => {
      try {
        const activeTurn = await agentService.getActiveTurn();
        const message = activeTurn?.request_payload.message?.trim();
        if (!activeTurn || !message || disposed) {
          return;
        }

        const thread = await agentService.getThread(activeTurn.thread_id);
        if (disposed) return;
        setCurrentThread(thread);
        currentThreadIdRef.current = thread.id;
        await loadThreadHistory(thread.id);
        if (disposed) return;

        const assistantMessageId = `assistant-restored-${activeTurn.turn_id}`;
        const placeholder: AgentMessage = {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          timestamp: new Date(activeTurn.created_at ?? Date.now()),
          isStreaming: true,
          turnStatus: "running",
        };
        setMessages((prev) => [...prev, placeholder]);

        restoreController = new AbortController();
        restoredTurnId = activeTurn.turn_id;
        abortControllerRef.current = restoreController;
        sharedAbortControllerRef.current = restoreController;
        activeTurnIdRef.current = activeTurn.turn_id;
        activeAssistantMessageIdRef.current = assistantMessageId;
        isLoadingRef.current = true;
        sharedIsLoadingRef.current = true;
        setIsLoading(true);

        let accumulatedContent = "";
        let terminalStatus: "completed" | "failed" | "cancelled" | null = null;
        let reconnectAttempt = 0;
        while (!restoreController.signal.aborted && terminalStatus === null) {
          if (reconnectAttempt > 0) {
            accumulatedContent = "";
          }
          try {
            const streamStatus = await agentService.streamMessage(
              activeTurn.thread_id,
              message,
              (chunk) => {
                if (disposed || restoreController?.signal.aborted) return;
                accumulatedContent += chunk;
                setMessages((prev) =>
                  prev.map((item) =>
                    item.id === assistantMessageId
                      ? { ...item, content: accumulatedContent }
                      : item
                  )
                );
              },
              activeTurn.request_payload.reasoning_mode ?? "fast",
              activeTurn.request_payload.forced_action,
              activeTurn.request_payload.project_ids,
              activeTurn.request_payload.document_scope,
              restoreController.signal,
              undefined,
              activeTurn.request_payload.interaction,
              activeTurn.turn_id,
              activeTurn.request_payload.edit_of_message_id,
              Boolean(activeTurn.request_payload.confirm_workspace_actions),
              activeTurn.request_payload.edit_of_message_id
                ? "/agent/chat/edit"
                : "/agent/chat",
            );
            terminalStatus =
              streamStatus === "running" || streamStatus === "superseded"
                ? streamStatus === "superseded" ? "failed" : null
                : streamStatus;
          } catch {
            if (restoreController.signal.aborted) break;
            try {
              const serverTurn = await agentService.getTurn(activeTurn.turn_id);
              terminalStatus =
                serverTurn.status === "running" || serverTurn.status === "superseded"
                  ? serverTurn.status === "superseded" ? "failed" : null
                  : serverTurn.status;
            } catch {
              terminalStatus = null;
            }
          }

          if (terminalStatus === null && !restoreController.signal.aborted) {
            reconnectAttempt += 1;
            await new Promise((resolve) =>
              setTimeout(resolve, Math.min(reconnectAttempt * 1000, 5000))
            );
          }
        }

        if (disposed || restoreController.signal.aborted) return;
        if (terminalStatus === "completed") {
          await loadThreadHistory(activeTurn.thread_id);
        } else {
          setMessages((prev) =>
            prev.map((item) =>
              item.id === assistantMessageId
                ? {
                    ...item,
                    isStreaming: false,
                    turnStatus: terminalStatus ?? "failed",
                    activity: undefined,
                  }
                : item
            )
          );
        }
      } catch (error) {
        console.error("Failed to restore active Agent Chat turn:", error);
      } finally {
        if (!disposed && restoredTurnId && activeTurnIdRef.current === restoredTurnId) {
          activeTurnIdRef.current = null;
          activeAssistantMessageIdRef.current = null;
          isLoadingRef.current = false;
          sharedIsLoadingRef.current = false;
          setIsLoading(false);
        } else if (!disposed && !restoredTurnId && !activeTurnIdRef.current) {
          isLoadingRef.current = false;
          sharedIsLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    };

    void restoreActiveTurn();
    return () => {
      disposed = true;
      restoreController?.abort();
      activeRestoreAttemptedRef.current = false;
    };
  }, [
    isInitialized,
    loadThreadHistory,
    sharedAbortControllerRef,
    sharedIsLoadingRef,
  ]);

  /**
   * Обработка клика по suggestion
   */
  const handleSuggestionClick = useCallback(
    (suggestion: AgentSuggestion) => {
      if (suggestion.disabled) {
        toast.info("This feature is coming soon!");
        return;
      }
      sendMessage(suggestion.prompt);
    },
    [sendMessage]
  );

  /**
   * Закрыть панель гайда
   */
  const closeGuidePanel = useCallback(() => {
    // Устанавливаем флаг, что гайд был показан, чтобы не открывать панель автоматически снова
    hasGuideBeenShownRef.current = true;
    setGuideContent(null);
    if (activePanel === "guide") {
      setActivePanel(null);
    }
    // Не обновляем guideContentRef, чтобы сохранить информацию о гайде для возможного ручного открытия
  }, [activePanel]);

  /**
   * Открыть панель гайда с указанным контентом
   */
  const openGuidePanel = useCallback((content: string, title?: string) => {
    guideContentRef.current = content;
    setGuideContent(content);
    setActivePanel("guide");
    if (title) {
      guideTitleRef.current = title;
      setGuideTitle(title);
    }
    // Не сбрасываем флаг - если пользователь закроет панель, она не должна открываться автоматически
  }, []);

  /**
   * Закрыть панель research context
   */
  const closeResearchContextPanel = useCallback(() => {
    hasResearchContextBeenShownRef.current = true;
    setResearchContextContent(null);
    if (activePanel === "research_context") {
      setActivePanel(null);
    }
  }, [activePanel]);

  /**
   * Открыть панель research context с указанным контентом
   */
  const openResearchContextPanel = useCallback((content: string, title?: string) => {
    researchContextContentRef.current = content;
    setResearchContextContent(content);
    if (title) {
      researchContextTitleRef.current = title;
      setResearchContextTitle(title);
    }
    setActivePanel("research_context");
  }, []);

  /**
   * Закрыть панель отчёта/цитаты
   */
  const closeReportPanel = useCallback(() => {
    setReportPanelContent(null);
    setReportPanelTitle(null);
    setReportPanelProjectId(null);
    setReportPanelRecordId(null);
    setReportPanelProjectReportId(null);
    setReportPanelReportType(null);
    setReportPanelAnchor(null);
    if (activePanel === "report") {
      setActivePanel(null);
    }
  }, [activePanel]);

  /**
   * Открыть панель отчёта с указанным markdown (для цитат и любых отчётов). options — начальный выбор проекта/record/отчёта.
   */
  const openReportPanel = useCallback(
    (
      content: string,
      title?: string,
      options?: {
        projectId?: string;
        recordId?: string;
        projectReportId?: string;
        reportType?: import("@/components/features/agent/components/report-panel").RecordReportType;
        anchor?: string;
      }
    ) => {
      setReportPanelContent(content);
      if (title) {
        setReportPanelTitle(title);
      } else {
        setReportPanelTitle(null);
      }
      setReportPanelProjectId(options?.projectId ?? null);
      setReportPanelRecordId(options?.recordId ?? null);
      setReportPanelProjectReportId(options?.projectReportId ?? null);
      setReportPanelReportType(options?.reportType ?? null);
      setReportPanelAnchor(options?.anchor ?? null);
      setActivePanel("report");
    },
    []
  );

  return {
    isThreadsLoaded: isInitialized,
    hasThreadsLoadError,
    hasHistoryLoadError,
    messages,
    isLoading,
    isCancelling,
    inputValue,
    currentThread,
    threads,
    guideContent,
    guideTitle,
    researchContextContent,
    researchContextTitle,
    reportPanelContent,
    reportPanelTitle,
    reportPanelProjectId,
    reportPanelRecordId,
    reportPanelProjectReportId,
    reportPanelReportType,
    reportPanelAnchor,
    activePanel,
    setInputValue,
    sendMessage,
    editMessage,
    cancelRequest,
    switchThread,
    createNewThread,
    renameThread,
    deleteThread,
    handleSuggestionClick,
    closeGuidePanel,
    openGuidePanel,
    closeResearchContextPanel,
    openResearchContextPanel,
    closeReportPanel,
    openReportPanel,
  };
}
